import React, { useRef, useState, useEffect } from "react"
import styled from "styled-components"
import { Link } from "react-router-dom";
import { motion } from 'framer-motion'

import img21 from '../../../assets/Aboutwfs/2.1.JPG';
import img22 from '../../../assets/Aboutwfs/2.2.JPG';
import img23 from '../../../assets/Aboutwfs/2.3.JPG';
import img24 from '../../../assets/Aboutwfs/2.4.JPG';
import img25 from '../../../assets/Aboutwfs/2.5.JPG';
import img26 from '../../../assets/Aboutwfs/2.6.JPG';

// Declare the BenefitImage variable
const BenefitImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const benefitsData = [
  {
    id: 1,
    number: "01",
    title: "Career Counseling",
    description:
      "Personalized guidance to help you choose the right aviation career path, from commercial pilot to specialized aviation roles, ensuring your goals align with industry opportunities.",
    image: img21,
  },
  {
    id: 2,
    number: "02",
    title: "Document Handling",
    description:
      "Complete assistance with all aviation documentation, from medical certificates to license applications, ensuring every requirement is met accurately and on time.",
    image: img22,
  },
  {
    id: 3,
    number: "03",
    title: "Visa & Immigration",
    description:
      "Expert support for student visas, work permits, and immigration processes across multiple countries, making your international aviation training seamless.",
    image: img23,
  },
  {
    id: 4,
    number: "04",
    title: "Logistics Support",
    description:
      "Comprehensive assistance with accommodation, transportation, and daily logistics, so you can focus entirely on your aviation training without distractions.",
    image: img24,
  },
  {
    id: 5,
    number: "05",
    title: "Parent & Guardian Support",
    description:
      "Dedicated support for families throughout the aviation training journey, providing regular updates and addressing concerns to ensure peace of mind.",
    image: img25,
  },
  {
    id: 6,
    number: "06",
    title: "After Departure Services",
    description:
      "Continued support even after training completion, including job placement assistance, career advancement guidance, and ongoing professional development.",
    image: img26,
  },
];

// Main section container with white background and padding
const SectionContainer = styled.section`
  background-color: #ffffff;
  padding: 100px 60px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 1024px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (max-width: 480px) {
    padding: 50px 20px;
  }
`

// Header container with flexbox layout for title and contact link
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 70px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 1024px) {
    margin-bottom: 60px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    align-items: flex-start;
    margin-bottom: 50px;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
    margin-bottom: 40px;
  }
`

// Main headline styling
const MainHeadline = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: #0284c7;
  margin: 0;
  line-height: 1.1;
  letter-spacing: -0.02em;
  
  @media (max-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    line-height: 1.15;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    line-height: 1.2;
  }
  
  @media (max-width: 360px) {
    font-size: 1.75rem;
  }
`

// Contact link with underline
const ContactLink = styled.a`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: #4b5563;
    text-decoration-color: #4b5563;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9375rem;
    white-space: normal;
  }
`

// Wrapper for the carousel to position buttons
const CarouselWrapper = styled.div`
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
`

const CarouselNavButton = styled.button`
  opacity: ${props => props.disabled ? 0.3 : 1};
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  pointer-events: ${props => props.disabled ? "none" : "auto"};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 10;
  transition: all 0.2s;
  color: #4b5563;
  
  &:hover {
    background-color: #f9fafb;
    box-shadow: 0 6px 16px rgba(0,0,0,0.15);
    color: #111827;
  }

  &.prev {
    left: -24px;
  }
  
  &.next {
    right: -24px;
  }

  /* Prevent selection text */
  user-select: none;

  @media (max-width: 1450px) {
    &.prev { left: -10px; }
    &.next { right: -10px; }
  }
  
  @media (max-width: 768px) {
    display: none; /* Hide on small screens and let user simply swipe */
  }
`

// Container for all benefit items
const BenefitsContainer = styled.div`
  display: flex;
  gap: 32px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome */
  }
  padding: 10px 0; /* Padding to ensure box-shadow is not clipped */
  
  /* Flex items take exactly 1/3 of space minus gaps */
  & > div {
    flex: 0 0 calc(33.333% - 21.33px);
    max-width: calc(33.333% - 21.33px);
    
    @media (max-width: 1100px) {
      flex: 0 0 calc(50% - 16px);
      max-width: calc(50% - 16px);
    }
    
    @media (max-width: 700px) {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`

// Card for each benefit — rounded, shadowed, and responsive
const BenefitCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
    border-color: #d1d5db;
  }
`

// Image container with proper aspect ratio
const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f3f4f6;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  ${BenefitCard}:hover & img {
    transform: scale(1.05);
  }
`

// Circular badge number overlay
const BenefitNumber = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.3s ease;

  ${BenefitCard}:hover & {
    background: rgba(0, 0, 0, 0.85);
    color: #ffffff;
    transform: scale(1.1);
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 0.9375rem;
    top: 12px;
    left: 12px;
  }
`

// Text content container
const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 18px;
    gap: 8px;
  }
`

// Benefit title styling
const BenefitTitle = styled.h3`
  font-size: 1.375rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`

// Benefit description styling
const BenefitDescription = styled.p`
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.7;
  margin: 0;
  word-wrap: break-word;
  hyphens: auto;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
    line-height: 1.65;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    line-height: 1.6;
  }
`

// removed MobileTopRow (no longer needed with vertical card layout)

// Separator line component
const Separator = styled.div`
  display: none;
  
  @media (max-width: 900px) {
    display: block;
    border-bottom: 1px solid #e0e0e0;
    margin: 30px 0;
  }
  
  @media (max-width: 768px) {
    display: block;
    border-bottom: 1px solid #e0e0e0;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    margin: 24px 0;
  }
`

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #6b7280;
  line-height: 1.7;
  margin: 24px 0 0 0;
  max-width: 900px;
  font-weight: 400;
  
  @media (max-width: 1024px) {
    font-size: 1.125rem;
    margin: 20px 0 0 0;
  }
  
  @media (max-width: 768px) {
    font-size: 1.0625rem;
    line-height: 1.65;
    margin: 16px 0 0 0;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
    margin: 14px 0 0 0;
  }
`

// Main component function
const Quickintro = () => {
  const carouselRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  useEffect(() => {
    const checkScroll = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setCanScrollPrev(scrollLeft > 0);
        setCanScrollNext(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };
    
    const current = carouselRef.current;
    if (current) {
      current.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      checkScroll();
    }
    
    return () => {
      if (current) current.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);



  const scrollPrev = () => {
    if (carouselRef.current) {
      const container = carouselRef.current;
      const scrollAmount = container.firstElementChild.clientWidth + 32;
      container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const scrollNext = () => {
    if (carouselRef.current) {
      const container = carouselRef.current;
      const scrollAmount = container.firstElementChild.clientWidth + 32;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <SectionContainer>
      {/* Header with title and contact link */}
      <HeaderContainer>
        <div>
          <MainHeadline>About WayForSky</MainHeadline>
          <SubHeading>
            From student to globally recognized pilot — with complete support.
          </SubHeading>
        </div>


<ContactLink as={Link} to="/contactus">
  Ready to get started? Contact us
</ContactLink>
      </HeaderContainer>

      {/* Benefits list container */}
      <CarouselWrapper>
        <CarouselNavButton className="prev" onClick={scrollPrev} disabled={!canScrollPrev} aria-label="Previous">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </CarouselNavButton>
        
        <CarouselNavButton className="next" onClick={scrollNext} disabled={!canScrollNext} aria-label="Next">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </CarouselNavButton>

        <BenefitsContainer ref={carouselRef}>
        {benefitsData.map((benefit, index) => (
          <motion.div
            key={benefit.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06, duration: 0.45, ease: 'easeOut' }}
          >
            <BenefitCard as={motion.div} whileHover={{ scale: 1.02 }}>
              <ImageContainer>
                <BenefitNumber>{benefit.number}</BenefitNumber>
                <img src={benefit.image || "/placeholder.svg"} alt={benefit.title} loading="lazy" />
              </ImageContainer>

              <TextContent>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </TextContent>
            </BenefitCard>
          </motion.div>
        ))}
        </BenefitsContainer>
      </CarouselWrapper>
    </SectionContainer>
  )
}

export default Quickintro
